
import type {Environment} from "header-bidding-base/modules/config/domain/environment";
import type {IConfigDTO} from "services-comun-meteored/modules/services/header-bidding/interface";

export interface GlobalConfigProps {
    project: {
        name: string;
        hash: string;
    }
    config: {
        name: string;
        hash: string;
    }
    env: Environment;
    log_level?: 'log' | 'warn' | 'error';
    domain: string;
    analytics?: {
        sampling?: number;
    }
}

interface CreateGlobalConfigProps extends Omit<GlobalConfigProps, 'env'>{
    env?: Environment;
}

const defaultGlobalConfigProps = {
    env: "development",
    analytics: {
        sampling: 1,
    }
}

export class GlobalConfig {

    private constructor(private props: GlobalConfigProps) {
        if (!props.project) {
            throw new Error("Project is required");
        }
        if (!props.config) {
            throw new Error("Config is required");
        }
        if (!props.env || !["production", "test", "development"].includes(props.env)) {
            throw new Error("Environment is required");
        }
    }

    public static create(props: CreateGlobalConfigProps): GlobalConfig {
        return new GlobalConfig({
            env: props.env || defaultGlobalConfigProps.env as Environment,
            analytics: props.analytics ? {
                ...props.analytics,
                sampling: props.analytics.sampling || defaultGlobalConfigProps.analytics.sampling,
            } : undefined,
            ...props,
        });
    }

    public static createFromDTO(dto: IConfigDTO): GlobalConfig {
        return this.create({
            project: {
                name: dto.project.name,
                hash: dto.project.hash,
            },
            config: {
                name: dto.config.name,
                hash: dto.config.hash,
            },
            domain: dto.config.domain,
            env: dto.config.environment as Environment,
            log_level: dto.config.log_level,
            analytics: dto.config.analytics && dto.config.analytics.enabled ? {
                sampling: dto.config.analytics.sampling,
            } : undefined
        });
    }

    public get project() {
        return this.props.project;
    }

    public get env(): "production" | "test" | "development" {
        return this.props.env;
    }

    public set env(env: "production" | "test" | "development") {
        this.props.env = env;
    }

    public get config() {
        return this.props.config;
    }

    public get domain() {
        return this.props.domain;
    }

    public get log_level() {
        return this.props.log_level;
    }

    public set log_level(log_level: GlobalConfigProps['log_level']) {
        this.props.log_level = log_level;
    }

    public get analytics(): GlobalConfigProps['analytics'] {
        return this.props.analytics;
    }

}
